/*
  EN — primary language and type source for all dictionaries.
  Voice per Master Operating Document V1.7: radically direct, architectural,
  operator-first, grounded. Banned words are banned here too.
*/
export const en = {
  langName: "English",

  nav: {
    signal: "Signal",
    vault: "Vault",
    systems: "Systems",
    tools: "Tools",
    studio: "Studio",
    about: "About",
    contact: "Contact",
    log: "Log",
  },

  dataPage: {
    label: "Data",
    langNote: "The Index is maintained in English — the citation language of the studies it tracks.",
  },

  audit: {
    seoTitle: "The Traffic-Free Income Audit — Free 60-Second Check | BEARTH",
    seoDesc:
      "12 yes/no questions to see how exposed your income is to the Google AI Overviews traffic collapse (38% fewer clicks, 72% zero-click searches). Instant verdict, no email required — built from the BEARTH SIGNAL Operator Audit.",
    label: "Operator Audit",
    title: "How exposed is your income?",
    lead: "Google AI Overviews are collapsing publisher traffic — proven causally by a Carnegie Mellon field experiment. 12 yes/no questions show where your money is still safe and where it isn't. 60 seconds. Your score appears instantly — no email needed.",
    stats: [
      { n: "−38%", l: "clicks per AI-Overview search" },
      { n: "72%", l: "of searches end without a click" },
      { n: "−60%", l: "Google traffic, small publishers" },
      { n: "−97%", l: "one major site, in 22 months" },
    ],
    statNote: "Sources: Agarwal & Sen (Carnegie Mellon/ISB) field experiment, Chartbeat via Axios, Growtika/Ahrefs — April 2026.",
    dataLink: "All studies on one citable page →",
    start: "Start the audit",
    noEmail: "No email required to see your score.",
    yes: "Yes",
    no: "No",
    back: "← Back",
    qOf: (i: number, n: number) => `Question ${i} of ${n}`,
    parts: { a: "Part A — Traffic source risk", b: "Part B — Income dependency risk", c: "Part C — Positioning risk" },
    questions: [
      "New clients, readers or buyers mostly find me through Google search — not through direct links, referrals or platform discovery.",
      "I rely on blog posts, SEO articles or landing pages optimized for Google to bring in leads or sales.",
      "I track my business health using Search Console impressions or organic clicks as a key metric.",
      "My content targets informational keywords (how-to, what-is, best-of) rather than direct-intent or brand searches.",
      "I monetize through display ads or affiliate links embedded in blog content.",
      "More than 30% of my monthly income depends on traffic volume rather than direct audience relationships.",
      "Potential clients discover my service through search queries — not word-of-mouth, platform search or community.",
      "I have fewer than 500 direct subscribers who would still see my content if my Google traffic dropped to zero.",
      "No inbound inquiry in the last 60 days came from a referral, share or community mention — only from search.",
      "I have no channel that is immune to Google's routing decisions (podcast, YouTube, newsletter, direct links).",
      "My content has no unique, citable expert voice — it answers generic questions AI can answer without crediting me.",
      "I have never been cited in an AI Overview or AI chatbot answer — AI systems don't recognize me as a source.",
    ],
    resultLabel: "Your verdict",
    scoreLine: (h: number) => `${h} of 5 high-risk answers`,
    breakdown: { high: "High risk", medium: "Medium risk", low: "Positioning" },
    verdicts: {
      critical: {
        name: "Critical exposure",
        situation: "Your primary income pipeline runs through Google. The 38% click collapse is already hitting you — or will within six months.",
        move: "Move 1 is urgent: start building one direct-audience channel this week. Email is the fastest — nothing else matters more right now.",
      },
      partial: {
        name: "Partial exposure",
        situation: "You have some search dependency, but you're not fully captured. You have time — not unlimited time.",
        move: "Move 2: identify which of your income streams are still safe and protect those first. Then build the direct channel.",
      },
      low: {
        name: "Low exposure",
        situation: "Your acquisition is mostly direct or platform-native. Google traffic is a bonus, not the engine.",
        move: "Move 3: position yourself as an AI-cited authority source — AI-referred visitors convert at multiples of standard organic traffic.",
      },
    },
    kitTitle: "Get the full Audit Kit",
    kitBody:
      "The complete kit contains the printable scorecard, the full research breakdown and the 3 Operator Moves in detail — including the channel table and the AI Citation Playbook.",
    snapshot: {
      label: "AI visibility snapshot",
      id: "Snapshot ID",
      primary: "First repair",
      layers: { high: "Search dependency", medium: "Income dependency", low: "Citation authority" },
      actions: {
        high: "Build one direct acquisition route before you publish more search-dependent content.",
        medium: "Protect the revenue path that still works when a search click disappears.",
        low: "Make the expertise concrete, dated and citable before you add more volume.",
      },
      captureTitle: "Keep the decision path",
      captureBody: "Optional. Your score is already visible; subscribe only if you want the next BEARTH implementation note by email.",
      captureHeadline: "Keep this snapshot",
      captureCta: "Keep snapshot + next steps",
      captureNote: "No score is held back. Unsubscribe anytime with one click.",
      copy: "Copy snapshot",
      copied: "Snapshot copied — save it with the first repair.",
    },
    emailCta: "Email me the next steps",
    buyKit: "Get the full kit · $49",
    vaultCta: "Or everything: Vault · $97",
    share: "Copy my result",
    shareCopied: "Copied — paste it anywhere.",
    shareText: (verdict: string, h: number) =>
      `My income exposure: ${verdict} (${h}/5 high-risk). 72% of Google searches now end without a click. Run the free 60-second audit: https://bearth.io/audit`,
    retake: "Retake the audit",
    disclaimer: "Information product, April 2026 data. Not business, legal or financial advice.",
  },

  log: {
    seoTitle: "Field Log — BEARTH | Phone-First Operator Notes",
    seoDesc:
      "Field notes from a phone-first operation: how this business runs phone-primary, the Android operator stack and why craft beats hustle. Written by Saimen Bearth, solo operator between Basel and Nairobi.",
    label: "Field Log",
    title: "Notes from the workshop.",
    lead: "Long-form field notes on running a phone-first operation — written from practice, not theory. English originals; the signal covers the rest.",
    read: "Read the note",
    minutes: "min read",
    back: "← All field notes",
    langNote: "Field notes are written in English — the project's working language.",
  },

  arms: {
    signal: "One signal at a time. No hype, no theory — only what you can deploy today.",
    vault: "Everything ever released under BEARTH — archived, searchable, compounding.",
    systems: "Playbooks and automation blueprints. Reproducible systems from a craftsman.",
    tools: "Gear for people who build with a phone — not with a team.",
    studio: "Brand, video and sound — from idea to finished asset.",
  },

  common: {
    live: "LIVE",
    building: "IN BUILD",
    inProduction: "In production",
    toPage: "Open →",
    scroll: "SCROLL",
    watchLatest: "Watch the episode",
    openGumroad: "Open Gumroad",
    toVault: "Open the Vault",
    baselTime: "BSL",
    nairobiTime: "NBO",
  },

  email: {
    label: "The signal · Free",
    placeholder: "you@your-domain.com",
    cta: "Get the signal",
    sending: "Sending …",
    done: "✓ Check your inbox",
    note: "No spam, no coach talk. Unsubscribe anytime — one click in every email.",
    consent: "I agree to receive the BEARTH SIGNAL email and know I can unsubscribe anytime.",
    consentRequired: "Please tick the consent box to subscribe.",
    privacyLink: "Privacy",
    invalid: "Enter a valid email address.",
    success: "Almost there — open the confirmation email we just sent and click the link.",
    unavailable: (mail: string) =>
      `Signup is temporarily unavailable. Email ${mail} if you need help.`,
    fail: (mail: string) => `Submission failed. Write directly to ${mail} — that always arrives.`,
    waitlistCta: "Join the waitlist",
  },

  footer: {
    claim: "One operator, phone-primary. BEARTH SIGNAL is the fastest way to follow the build.",
    portfolio: "Portfolio",
    channels: "Channels",
    direct: "Direct",
    aboutLink: "About Saimen",
    contactLink: "Contact",
    imprint: "Imprint",
    privacy: "Privacy",
    terms: "Terms",
    editorialAi: "Editorial & AI",
  },

  home: {
    seoTitle: "BEARTH — Saimen Bearth · One Operator, Phone-Primary, Zero Team",
    seoDesc:
      "AI briefing (SIGNAL), drop archive (VAULT), playbooks and tools by solo operator Saimen Bearth — built phone-primary between Basel and Nairobi.",
    kicker: "The Phone-First Operator",
    karibu: "Karibu — welcome to the workshop",
    heroA: "Carpenter. Barkeeper. Digital nomad. I built ",
    heroBrands: "five arms",
    heroAnd: " and ",
    heroFollowers: "30K+ followers",
    heroB: " — phone-primary, between Basel and Nairobi.",
    promise:
      "I check where your income is exposed to AI search — and hand you the deployable fix. One signal, one product. Built solo, phone-primary.",
    ctaAudit: "Run the free 60-second audit",
    ctaPortfolio: "My portfolio",
    ctaSignal: "Get the signal →",
    shelf: {
      label: "Get it today",
      title: "Three doors. One direction.",
      lead: "Everything on this shelf has a clear next step today.",
      free: {
        badge: "Free",
        name: "Traffic-Free Income Audit",
        desc: "12 questions, instant verdict, no email. See where your income is exposed before it costs you.",
        cta: "Run the free audit",
      },
      kit: {
        badge: "$49",
        name: "AI Search Blindness Fix Kit",
        desc: "The deployable fix: step-by-step SOP, tested prompts, machine-readability checklist. Phone-friendly, no team needed.",
        cta: "Get the kit · $49",
      },
      vault: {
        badge: "$97",
        name: "BEARTH Vault",
        desc: "Every drop, past and future, in one archive. One payment, no subscription — the price rises as the catalog grows.",
        cta: "Lock the founder price",
      },
    },
    stats: [
      { n: "30K+", l: "TikTok followers" },
      { n: "5", l: "Arms, one brand" },
      { n: "Basel ↔ Nairobi", l: "Operating base" },
      { n: "Phone-Primary", l: "Core infrastructure" },
    ],
    about: {
      chip: "Who I am",
      title: ["Craft as a", "way of thinking."],
      p1: "I'm a carpenter, a barkeeper and a digital nomad. The building site taught me one thing: systems have to work when nobody is watching.",
      p2: "At 35 I have no team, no office, no investors. I run phone-primary, with one system and consistent execution. Everything under BEARTH was built that way — between Zurich, Basel and Nairobi.",
      p3: "The goal: infrastructure that runs while I sleep. In Switzerland and in Kenya at the same time.",
      tags: ["Craft & construction", "AI & automation", "Content production", "Digital nomad", "Solo operator"],
      more: "The full story — about Saimen",
    },
    portfolio: {
      chip: "Portfolio",
      title: "What I've built.",
      lead: "One brand, five arms. All under the BEARTH roof — every card leads to that arm's page.",
    },
    hook: ["AI won't replace solo operators.", "Slow execution will."],
    auditTeaser: {
      kicker: "Free · 60 seconds · No email",
      titleA: "72% of Google searches end",
      titleEm: "without a click.",
      body: "Is your income built on traffic that's disappearing? 12 yes/no questions, instant verdict — built from the BEARTH SIGNAL Operator Audit and a Carnegie Mellon field experiment.",
      cta: "Run the audit",
    },
    machine: {
      label: "The machine",
      title: "You see the output. The machine stays in the shop.",
      lead: "Every episode runs through a purpose-built pipeline — versioned, audited, hardened through repeated production and review. The results are public. The internals aren't.",
      steps: [
        { t: "Research Engine", v: "v1.2", d: "Scans 24–48 hours of AI and remote-work movement, scores it, keeps exactly one monetizable signal." },
        { t: "SOP Machine", v: "v4.4", d: "Turns the signal into a sellable product blueprint before a single line is scripted." },
        { t: "Scriptwriter", v: "v4.6", d: "Writes the 10-minute briefing against twelve audited quality gates — voice, retention, disclosure." },
        { t: "Avatar Render", v: "HeyGen", d: "An openly disclosed AI avatar delivers the briefing. Set up once, reused every release." },
        { t: "Drop + Vault", v: "$49 / $97", d: "The product ships to Gumroad, the archive compounds — drop by drop." },
      ],
      moat: "Copy the idea if you want — the moat is hundreds of locked decisions, repeated reps and a pipeline you can't download.",
      videoCaption: "The operator at work — phone-first, studio optional.",
    },
    worlds: {
      kicker: "Two anchors · One operation",
      titleA: "Basel builds the",
      titleEm: "precision.",
      titleB: "Nairobi sets the",
      titleEm2: "pace.",
      body: "Swiss craft discipline and Nairobi's speed run the same operation. The phone works in both places — the signal doesn't care about the time zone.",
    },
    episode: {
      chip: "Episode 01 — Live",
      title: ["AI Search Blindness", "Fix Kit"],
      descA: "How to actually use AI search engines — and why 90% of operators miss the biggest lever. The first edition of ",
      descLink: "BEARTH SIGNAL",
      descB: ".",
      watch: "Watch the episode →",
      buy: "Get the kit · $49",
      vault: "Vault · $97",
    },
    faq: {
      label: "Frequent questions",
      title: "Answered directly.",
      items: [
        {
          q: "What is BEARTH?",
          a: "BEARTH is the umbrella brand of Swiss solo operator Saimen Bearth — carpenter, barkeeper and digital nomad between Basel and Nairobi. Five arms run under BEARTH: BEARTH SIGNAL (an AI briefing for solo operators, live), BEARTH VAULT (the growing archive of every drop, $97 lifetime), BEARTH SYSTEMS (playbooks and automation blueprints), BEARTH TOOLS (micro-tools and prompt kits) and BEARTH STUDIO (brand, video, sound). Everything is operated by one person — no team, no office, no investors — phone-primary, from an Android phone. The name states the principle: Be Earth, stay grounded. Craftsman logic — measure, cut, join, check — translated into digital systems that keep working when nobody is watching.",
        },
        {
          q: "What is a solo operator?",
          a: "A solo operator is a person who runs a business alone — not through more working hours, but through systems: automations, reusable templates, AI tools and clear procedures replace the team. The difference from a classic freelancer: a freelancer sells hours, a solo operator builds infrastructure that keeps running without them. BEARTH is aimed exactly at this group — independents, freelancers moving toward a product business, and digital nomads — and shows on its own example how a one-person, phone-primary operation can build a multi-arm brand. No coaching, no motivational filler: one concrete signal at a time and one tool you can deploy immediately.",
        },
        {
          q: "What is a phone-first operator?",
          a: "A phone-first operator is an entrepreneur who runs their entire business from a smartphone — research, production, publishing, sales and support — using the phone not as a companion device but as the primary infrastructure. The term comes from BEARTH, where the operation runs phone-primary — historically documented on a Samsung S20 FE: the BEARTH SIGNAL briefing is researched with Perplexity, rendered as an openly disclosed HeyGen avatar video, cut in CapCut, published to YouTube and sold through Gumroad — phone-primary, without an office or a team. The constraint is deliberate: what works on a phone works anywhere, survives any border crossing and forces radical simplicity. Phone-first is not a budget compromise; it's an operating discipline.",
        },
        {
          q: "Where should I start with BEARTH?",
          a: "The fastest entry is BEARTH SIGNAL, the only arm currently live: a briefing that turns one structural shift in AI, automation or remote work into one concrete, immediately usable step — as a YouTube video, an email and a ready-made $49 product on Gumroad. Episode 1, the “AI Search Blindness Fix Kit”, is already available. If you want everything at once, the BEARTH VAULT is the cleaner move: the archive where every drop lands, searchable and open with a single $97 purchase. The remaining arms — SYSTEMS, TOOLS, STUDIO — open only once SIGNAL carries itself; all three have waitlists on their pages.",
        },
      ],
    },
    qualify: {
      chip: "Work with me",
      title: ["BEARTH is not", "for everyone."],
      lead: "If you're a solo operator building real systems — no hype, no coaching business — let's talk. If you're looking for a team or an agency: wrong address.",
      cta: "I'm a fit — reach out",
    },
  },

  signal: {
    seoTitle: "BEARTH SIGNAL — The AI Briefing for Solo Operators",
    seoDesc:
      "One signal at a time from AI, automation and remote work — turned into a 10-minute briefing, a deployable $49 product and an email. Built phone-primary.",
    name: "BEARTH SIGNAL",
    tagline: "AI · Operator · Briefing",
    h1: "One signal. One deployable product.",
    intro:
      "BEARTH SIGNAL tracks the structural shifts in AI, automation and remote work — and turns exactly one of them per release into a 10-minute briefing, a Gumroad drop and a Vault asset. Built phone-primary. No coach talk, no funnels.",
    flowLabel: "The production run",
    flowTitle: "How a signal becomes a product.",
    flow: [
      { t: "Research", d: "Each run starts with deep research over the last 24–48 hours and pulls the strongest operator signal." },
      { t: "Briefing", d: "A 10-minute YouTube video: hook, evidence, concrete step. AI use openly disclosed." },
      { t: "Drop", d: "The same signal becomes a $49 product on Gumroad: deployable SOP, prompts, templates." },
      { t: "Discount code", d: "Time-limited discount codes for the current drop are announced on Instagram and Facebook when a drop ships." },
      { t: "Email", d: "Subscribers get the signal and the drop link before everyone else — straight to the inbox." },
    ],
    tiersLabel: "Access",
    tiersTitle: "Three doors. No forced subscription.",
    tierBadge: "The drop",
    tiers: [
      {
        name: "Free briefing",
        price: "$0",
        desc: "The YouTube briefing plus the free email signal.",
        cta: "Latest episode",
      },
      {
        name: "The drop",
        price: "$49",
        desc: "Today's deployable product on Gumroad — SOP, prompts, templates.",
        cta: "Open Gumroad",
      },
      {
        name: "BEARTH VAULT",
        price: "$97",
        desc: "Every past and future drop in one archive — one purchase, no subscription.",
        cta: "Open the Vault",
      },
    ],
    codeNote:
      "Discount: time-limited codes for the current drop (format SIGNAL + date, max. 500 uses) are announced on Instagram and Facebook around release — check the pinned post there.",
    dropLabel: "Current drop",
    dropTitle: "Episode 01: AI Search Blindness Fix Kit.",
    dropBody:
      "“AI search blindness” is the problem of content simply not appearing in AI search systems like ChatGPT, Perplexity and Google AI Overviews — not because it's bad, but because it isn't readable, quotable or findable for language models. The first episode's fix kit shows step by step how solo operators check and reclaim their visibility in these systems: tested prompts, a checklist for machine-readable content structure and templates that work without a team and without extra software.",
    watchCta: "Watch the briefing",
    buyCta: "Get the kit · $49",
    subLabel: "Subscribe",
    subNote: "New episodes and drop links land in subscribers' inboxes first.",
    subCta: "Push it to my inbox",
    stack: {
      label: "Operator stack",
      title: "The stack that runs this.",
      lead: "Four tools carry the daily production — tested in the live operation, not in a listicle. Marked links are affiliate links: they cost you nothing and fund the runway.",
      roles: [
        "Deep research — the morning scan",
        "AI avatar & voice — openly disclosed",
        "Edit & captions — on the phone",
        "Agentic research backup",
      ],
      note: "Affiliate disclosure: marked links support BEARTH at no extra cost to you.",
    },
    faqLabel: "Questions",
    faqTitle: "Answered directly.",
    faq: [
      {
        q: "What is BEARTH SIGNAL?",
        a: "BEARTH SIGNAL is a briefing for solo operators — independents who run their business alone through systems instead of a team. Per release, one structural shift in AI, automation or remote work is selected and translated into three formats: a roughly ten-minute YouTube video, an email to subscribers and a deployable $49 product on Gumroad with standard operating procedures (SOPs), prompts and templates. Everything is produced by Saimen Bearth alone — phone-primary, on an Android phone between Basel and Nairobi. The use of AI tools — including the AI avatar in the videos — is openly disclosed. No coaching, no nine-step funnels: information, one concrete step, one tool.",
      },
      {
        q: "What does BEARTH SIGNAL cost?",
        a: "There are three doors. First: the YouTube briefing and the email signal are free — subscribing to @bearthsignal or joining the email list is enough. Second: the drop, the product package belonging to each signal, costs $49 on Gumroad. Time-limited discount codes (valid 24 hours, capped at 500 uses, format SIGNAL plus the date) are announced on Instagram and Facebook around release. Third: the BEARTH VAULT bundles every past and future drop for a one-time $97 — no subscription. The Vault price rises as the catalog grows; the earliest entry is the cheapest one.",
      },
      {
        q: "Who is BEARTH SIGNAL for — and who is it not for?",
        a: "SIGNAL is aimed at solo operators, freelancers moving toward a product business and digital nomads who work without a team and execute decisions themselves. It's useful for anyone who doesn't just want to follow AI developments but to pull one concrete step out of them every day — with tools that work on a phone. It is not made for agencies, teams with approval chains or anyone looking for motivational content: there are no coaching calls, no community ceremonies and no promises of easy money. The standard is a craftsman's: does the tool work today, in a real one-person operation? If yes, it goes into the briefing. If not, it doesn't.",
      },
      {
        q: "What exactly is in a drop?",
        a: "Every drop is a self-contained work package for the day's signal: a step-by-step SOP (standard operating procedure), the tested prompts in copy-paste format, templates (e.g. sheets, checklists or automation blueprints) and — where useful — a short video walkthrough. Example, episode 1, the “AI Search Blindness Fix Kit”: it shows how AI search systems like ChatGPT, Perplexity and Google AI Overviews select content, where your visibility typically gets lost and how to reclaim it with concrete steps. All drops are built to be deployable without extra software and without a team — phone-friendly, in hours instead of weeks. Every drop then lands permanently in the BEARTH VAULT.",
      },
    ],
  },

  vault: {
    seoTitle: "BEARTH VAULT — Every Drop. One Library. $97 Lifetime",
    seoDesc:
      "The BEARTH VAULT collects every BEARTH SIGNAL drop in a searchable lifetime archive: SOPs, prompts and playbooks for solo operators. One $97 purchase instead of $49 per drop — founder price for the waitlist.",
    name: "BEARTH VAULT",
    tagline: "Every Drop. One Library.",
    h1: "Every signal. Every system. Stacked.",
    introA: "The BEARTH VAULT is the long-term archive of every ",
    introLink: "BEARTH SIGNAL drop",
    introB:
      ". One door, one growing arsenal: today's drop is tomorrow's cornerstone — and Vault holders get all of it for one $97 purchase instead of $49 per drop.",
    whyLabel: "The principle",
    whyTitle: "Why an archive instead of a subscription.",
    value: [
      {
        t: "One key",
        d: "A single $97 purchase opens every past and future drop. No subscription, no recurring charges.",
      },
      {
        t: "Deployable only",
        d: "No coaching material. Exclusively SOPs, prompts, sheets and playbooks that work today.",
      },
      {
        t: "Compounding",
        d: "Every drop raises the archive's value. The price grows with the catalog — the earliest entry is the cheapest.",
      },
    ],
    openCta: "Open the Vault on Gumroad",
    catalogLabel: "Catalog",
    catalogTitle: "The inventory.",
    catalogEp1: "AI Search Blindness Fix Kit",
    catalogPending: "In production — topic follows the signal",
    catalogNote: "The catalog grows with every signal. What's listed here exists — what doesn't exist isn't announced.",
    founderLabel: "Founder price",
    founderTitle: "Lock the price before the catalog grows.",
    founderBody:
      "The Vault price rises with every additional drop. Leave your email and you get the founder price on opening day — before the first increase.",
    founderCta: "Lock the founder price",
    compareLabel: "The math",
    compareTitle: "When the Vault pays for itself.",
    compareLead: "Real numbers, not a sales trick: at $49 per drop, the Vault is already cheaper after two drops.",
    compareDaysLabel: "Drops bought",
    compareDailyLabel: "Paying per drop ($49)",
    compareVaultLabel: "Vault ($97 once)",
    compareNote: "After the second drop, every further drop is free inside the Vault.",
  },

  arm: {
    whatLabel: "What's being built",
    whatTitle: "The bench plan.",
    gateLabel: "Opening condition",
    gateNote: "No artificial countdown — the arm goes live when it carries weight.",
    faqLabel: "Questions",
    faqTitle: "Answered briefly.",
    waitLabel: "Waitlist",
    waitTitle: "Hear it first when it opens.",
    waitBody: (name: string) =>
      `Sign up and you'll get the first release of ${name} before anyone else — together with BEARTH SIGNAL, where the build is documented.`,
    waitSignal: "Until then: watch BEARTH SIGNAL",
  },

  systems: {
    seoTitle: "BEARTH SYSTEMS — Playbooks & Automations for Solo Operators",
    seoDesc:
      "BEARTH SYSTEMS turns the daily production run behind BEARTH SIGNAL — research, script, video, drop, distribution — into clonable playbooks and automation templates. Currently in build, waitlist open.",
    headline: "Playbooks. Automations. Operator OS.",
    intro:
      "BEARTH SYSTEMS translates the daily production run behind BEARTH SIGNAL — research, script, video, drop, distribution — into reproducible playbooks other solo operators can clone. None of it is theory: every system runs for months in the live operation first.",
    items: [
      { t: "Make.com / n8n templates", d: "Ready-to-import automation scenarios — from content repurposing to lead capture." },
      { t: "Notion & Sheets control rooms", d: "Command centers for planning, production and publishing — one overview instead of ten tabs." },
      { t: "2-hour production SOP", d: "The complete daily run of BEARTH SIGNAL as a step-by-step guide, phone-friendly." },
      { t: "ManyChat & email automations", d: "DM code delivery, welcome sequences and broadcast templates, ready to adopt." },
      { t: "First public drop: SIGNAL Studio Stack", d: "The complete production system behind the daily briefing as the first public playbook." },
    ],
    gate: "BEARTH SYSTEMS opens once BEARTH SIGNAL carries itself — the playbooks are sold only after they've proven themselves in the live operation.",
    faq: [
      {
        q: "What separates BEARTH SYSTEMS from course platforms?",
        a: "There are no courses. BEARTH SYSTEMS delivers the actually running systems of a one-person operation as copyable templates: automation scenarios, control rooms, SOPs. You're not buying knowledge about systems — you're buying the systems themselves, including the places where they break in daily use and how that gets fixed.",
      },
      {
        q: "Do I need a laptop for this?",
        a: "No. Every playbook comes from an operation that runs phone-primary on Android (historically a Samsung S20 FE). Make.com, n8n (cloud), Notion, Google Sheets and ManyChat all work in the browser or as apps. Where a bigger screen genuinely speeds things up, the SOP says so explicitly.",
      },
    ],
  },

  tools: {
    seoTitle: "BEARTH TOOLS — Gear for Phone-First Builders",
    seoDesc:
      "BEARTH TOOLS is the toolmaking arm of BEARTH: AI micro-tools, prompt kits and decision sheets for solo operators who build from a phone. Currently in build — every tool is tested in the live operation first.",
    headline: "Tools. Sharpened first, sold second.",
    intro:
      "BEARTH TOOLS is the workbench corner of BEARTH: small, sharp tools for people who build with a phone instead of a team. Every tool passes the same test as a chisel in the carpentry shop — it's only sold once it's been in daily use in the live operation.",
    items: [
      { t: "AI micro-tools", d: "Small, focused helpers for recurring operator tasks — one tool per problem, no Swiss army knife." },
      { t: "Prompt kits", d: "Tested prompt collections for Perplexity, ChatGPT and Genspark — including the cases where they fail." },
      { t: "Voice & avatar pipelines", d: "Documented HeyGen workflows for voice and avatar — from raw text to finished video, phone-friendly." },
      { t: "Calculators & decision sheets", d: "Lightweight calculators for pricing, time and go/no-go decisions in a one-person operation." },
    ],
    gate: "BEARTH TOOLS goes public once BEARTH SIGNAL has funded the runway — no tool ships that hasn't run for weeks in the daily operation first.",
    faq: [
      {
        q: "Why “phone-first” — is a phone really enough?",
        a: "The entire BEARTH operation has run phone-primary from day one — Android as primary infrastructure, no dedicated studio. The constraint is deliberate: what works on the phone works everywhere. Every BEARTH tool inherits that requirement.",
      },
    ],
  },

  studio: {
    seoTitle: "BEARTH STUDIO — Brand, Video & Sound from One Hand",
    seoDesc:
      "BEARTH STUDIO is the visual arm of BEARTH: brand identity, thumbnail systems, video templates and sound design — developed on the channel itself. Opens after 30 produced SIGNAL episodes, waitlist open.",
    headline: "Brand. Video. Sound. Story.",
    intro:
      "BEARTH STUDIO is the identity arm: everything visible and audible about the brand — covers, thumbnails, templates, sound. The studio works exclusively for BEARTH first; what survives repeated real production there opens up afterwards as template systems.",
    items: [
      { t: "Brand identity systems", d: "Complete brand systems on the BEARTH pattern: one umbrella brand, clearly separated sub-brand colors, one rulebook." },
      { t: "Thumbnail systems", d: "Repeatable thumbnail logic for daily production — recognition before one-off optimization." },
      { t: "Short & longform templates", d: "Editing templates for daily video production on a phone — CapCut-based, timed to the minute." },
      { t: "AI avatar pipelines", d: "Documented workflows for avatar video with open AI disclosure — from script to upload." },
      { t: "Sound & audio kit", d: "A recognizable audio signature: intro, transitions, beds — built once, reused across releases." },
    ],
    gate: "BEARTH STUDIO opens once SIGNAL production reaches 30 completed episodes — only then is every template hardened under real conditions.",
    faq: [
      {
        q: "Does BEARTH STUDIO take client work?",
        a: "Not yet. The studio produces exclusively for the five BEARTH arms first — that's the stress test. After 30 produced SIGNAL episodes, the template systems open first, for self-service use; classic commissioned work stays deliberately excluded, because BEARTH runs without a team and without calls.",
      },
    ],
  },

  about: {
    seoTitle: "About Saimen Bearth — Carpenter, Barkeeper, Solo Operator",
    seoDesc:
      "Saimen Bearth, 35, runs BEARTH phone-primary between Basel and Nairobi — no team, no office, no investors. Background, working principles and the BEARTH brand system in detail.",
    label: "Operator brief",
    h1: "Saimen Bearth. Craftsman logic, digital output.",
    facts: [
      { t: "Background", d: "Carpenter · Construction · Barkeeper. Years on the site and behind the bar — zero tolerance for wasted moves." },
      { t: "Geography", d: "Basel (Switzerland) ↔ Nairobi (Kenya). Double base, one operation. The phone works the same in both places." },
      { t: "Device stack", d: "Android, phone-primary — currently a Samsung S26, with a MacBook available when the task genuinely needs it. The operating default stays mobile." },
      { t: "AI stack", d: "Perplexity, HeyGen, CapCut, Genspark — rotated monthly. The best tool for the step at hand." },
    ],
    bio: [
      "My name is Saimen Bearth. I'm 35, I live between Basel and Kenya, and I run this entire build phone-primary, on Android.",
      "My background is physical: carpenter, construction worker, barkeeper. I spent years building things with my hands — structures that had to stand, systems that had to function, rooms people actually used. That discipline doesn't come off when you go digital.",
      "BEARTH is not a rebrand of me as a coach or content creator. It's the direct translation of craftsman logic into a digital operation. Same principles — measure twice, cut once, build for load, ship when it holds. Different material: AI tools, video, Gumroad, email.",
      "For each release I select one structural signal in AI, automation or remote work, translate it into a briefing and turn the strongest buyer need into a deployable product and distribution package. No team. No venture capital. No calls, no coaching packages. The operator stays in the workshop — the output ships itself.",
      "The goal: a compounding operation that grows with every drop — built on Android, run with craftsman precision, sound for decades.",
    ],
    bioHighlight: 4,
    principlesLabel: "Working principles",
    principlesTitle: "Seven rules that run the operation.",
    principles: [
      { rule: "One signal, one step", detail: "No multitasking across five sites. One AI signal at a time, fully executed — then the next." },
      { rule: "The phone is enough", detail: "Whatever can't be done from the Android phone gets cut. The constraint is the feature." },
      { rule: "Ship when it holds", detail: "Release only after the evidence, product and delivery path pass their gates. Speed never replaces truth." },
      { rule: "Everything compounds", detail: "Every drop lands in the Vault, every email grows the list. Nothing is used only once." },
      { rule: "No coach talk", detail: "No nine-step frameworks, no motivational content. Information density first, always." },
      { rule: "Basel ↔ Nairobi", detail: "Two anchors, one identity. Grounded in craft, open to wherever the signal leads." },
      { rule: "Build for decades", detail: "No sprint. Every system is built to still run in ten years without supervision." },
    ],
    brandLabel: "Brand system",
    brandTitle: "One brand. Five arms.",
    brandLead:
      "BEARTH is the umbrella brand. Five arms share one identity, one voice and one color system — every color is extracted directly from the real logo files. Only one arm is live right now: BEARTH SIGNAL. The rest open on schedule.",
    paletteLabel: "Palette · Pixel-extracted",
    paletteNames: { base: "Base", cream: "Cream", signal: "Signal", systems: "Systems", tools: "Tools", studio: "Studio" },
    typoNote:
      "Typography: Barlow Condensed (display, caps) and Inter (body). Form language: 1px lines, 3px radius, no gradients — sawn, not poured.",
    seeSignal: "See BEARTH SIGNAL live",
    subLabel: "Follow along",
    subTitle: "The way into the workshop.",
    subBody: "The fastest way to follow the build is the email signal. Email is the only channel that survives every platform change.",
    subNote: "No spam, no coaching offers. Just the signal and the drop.",
    subCta: "Get the signal",
  },

  contact: {
    seoTitle: "Contact — BEARTH | Async, No Calls",
    seoDesc:
      "BEARTH works asynchronously: one clear message instead of a booking link. Contact for licensing, partnerships and press — via form or directly to saimen@bearth.io. No consulting calls, no coaching.",
    label: "Contact",
    h1: "One door. One inbox. No calls.",
    intro:
      "BEARTH runs no consulting calls, no coaching sessions and no client projects. The fastest path is asynchronous: leave a clear message, a reply comes when one is due. For licensing, partnerships or press, use the same address — with the right subject line.",
    form: {
      name: "Name",
      namePlaceholder: "Your name",
      email: "Email",
      message: "Message",
      messagePlaceholder: "What's it about — and what's the concrete next step?",
      send: "Send message",
      sending: "Sending …",
      sent: "✓ Sent",
      note: "Delivered through BEARTH's own Hostinger infrastructure to saimen@bearth.io; you receive an automatic acknowledgement. No marketing subscription is created — details in the privacy policy.",
      success: "Received. A reply follows as soon as one is due.",
    },
    channels: { email: "Email", youtube: "YouTube", instagram: "Instagram", web: "Web" },
    responseNote:
      "Messages are reviewed asynchronously without a fixed response-time promise. Published work appears on YouTube and TikTok at @bearthsignal.",
  },

  notFound: {
    label: "Error 404",
    body: "This page doesn't exist — or doesn't exist yet. At BEARTH, only what's actually built gets shown.",
    home: "Back to the start",
    signal: "To the signal",
  },

  legalNote: "Legal pages are provided in English and German; the German version is authoritative.",
};

export type Dict = typeof en;
